import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Api as ApiIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Lock as LockIcon,
  Speed as SpeedIcon,
  Code as CodeIcon,
  HelpOutline as HelpIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';

const APIGuidelines = ({ projectId, existingContent = '', onSave }) => {
  const renderAPIGuidelinesPreview = (content) => {
    // Try to parse as JSON first for structured viewing
    try {
      const apiData = JSON.parse(content);
      return renderStructuredAPI(apiData);
    } catch (e) {
      // If not JSON, render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render a structured API visualization
  const renderStructuredAPI = (apiData) => {
    return (
      <Box>
        {apiData.endpoints && apiData.endpoints.length > 0 ? (
          <Box>
            {apiData.endpoints.map((endpoint, index) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Chip 
                      label={endpoint.method || 'GET'} 
                      size="small"
                      color={
                        endpoint.method === 'GET' ? 'primary' :
                        endpoint.method === 'POST' ? 'success' :
                        endpoint.method === 'PUT' ? 'warning' :
                        endpoint.method === 'DELETE' ? 'error' : 'default'
                      }
                      sx={{ mr: 2, minWidth: 60, textAlign: 'center' }}
                    />
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {endpoint.path || '/api/resource'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {endpoint.description || 'API endpoint'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {endpoint.description || 'No description provided'}
                    </Typography>
                    
                    {endpoint.parameters && endpoint.parameters.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Parameters
                        </Typography>
                        <List dense>
                          {endpoint.parameters.map((param, paramIndex) => (
                            <ListItem key={paramIndex} sx={{ py: 0 }}>
                              <ListItemText 
                                primary={
                                  <Box component="span">
                                    <strong>{param.name}</strong> 
                                    <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                                      ({param.type || 'string'})
                                      {param.required && 
                                        <Box component="span" sx={{ ml: 1, color: 'error.main' }}>
                                          required
                                        </Box>
                                      }
                                    </Box>
                                  </Box>
                                }
                                secondary={param.description || 'No description'} 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    
                    {(endpoint.responses || []).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Responses
                        </Typography>
                        <List dense>
                          {endpoint.responses.map((response, respIndex) => (
                            <ListItem key={respIndex} sx={{ py: 0 }}>
                              <ListItemText 
                                primary={
                                  <Box component="span">
                                    <Chip 
                                      label={response.code || 200} 
                                      size="small"
                                      color={
                                        response.code < 300 ? 'success' :
                                        response.code < 400 ? 'info' :
                                        response.code < 500 ? 'warning' : 'error'
                                      }
                                      sx={{ mr: 1 }}
                                    />
                                    {response.description || 'Success'}
                                  </Box>
                                }
                                secondary={response.schemaDescription || 'Response schema'} 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center">
            No API endpoints defined
          </Typography>
        )}
      </Box>
    );
  };
  
  // API guidelines prompt configuration
  const apiGuidelinesPromptConfig = {
    system: "You are an API design expert with deep knowledge of RESTful API design principles, GraphQL, and API best practices.",
    examples: [
      "What should our API naming conventions be?",
      "How should we handle versioning in our RESTful API?"
    ],
    instruction: "Provide detailed API design guidelines, standards, and best practices based on industry recommendations."
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="apiGuidelines"
      title="API Guidelines"
      icon={ApiIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderAPIGuidelinesPreview}
      promptConfig={apiGuidelinesPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown,.md"
    >
      {/* Additional API guidelines content */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>API Design Principles</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LinkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">RESTful Resources</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Design APIs around resources. Use nouns, not verbs. Use HTTP methods appropriately.
                </Typography>
                <Chip 
                  label="Resource-Oriented" 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LockIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Security First</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Always use HTTPS. Implement proper authentication. Use API keys or OAuth tokens.
                </Typography>
                <Chip 
                  label="Secure by Design" 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SpeedIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Performance</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Use pagination for large collections. Support filtering and sorting. Implement caching.
                </Typography>
                <Chip 
                  label="Optimized" 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>API Implementation Options</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <LanguageIcon color="primary" sx={{ mt: 0.5, mr: 2 }} />
              <Box>
                <Typography variant="subtitle1" gutterBottom>RESTful API</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Resource-oriented API design using HTTP methods and status codes. Simple to understand and implement.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="GET" size="small" color="primary" />
                  <Chip label="POST" size="small" color="success" />
                  <Chip label="PUT" size="small" color="warning" />
                  <Chip label="DELETE" size="small" color="error" />
                </Box>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <CodeIcon color="primary" sx={{ mt: 0.5, mr: 2 }} />
              <Box>
                <Typography variant="subtitle1" gutterBottom>GraphQL</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Query language for APIs that enables clients to request exactly the data they need. Reduces over-fetching and under-fetching.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Query" size="small" color="primary" />
                  <Chip label="Mutation" size="small" color="success" />
                  <Chip label="Subscription" size="small" color="warning" />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Documentation Tools</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <HelpIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="OpenAPI (Swagger)" 
                secondary="Industry standard for documenting RESTful APIs. Generate documentation, client SDKs, and server stubs." 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <HelpIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="GraphQL Schema" 
                secondary="Self-documenting API with introspection. Tools like GraphiQL provide interactive documentation." 
              />
            </ListItem>
          </List>
        </Box>
      </Paper>
    </DocumentTypeTemplate>
  );
};

export default APIGuidelines;